import type { LoyaltyRedeemCopy } from "../../components/global/Modal/Modals/LoyaltyRedeemPopup/LoyaltyRedeemPopup";
import { TierChangeKind } from "../../components/global/Modal/Modals/LoyaltyTierChange/tierChangeKind";
import LoyaltyOffer from "../LoyaltyOfferClass/LoyaltyOffer";
import { createLoyaltyOffer } from "../LoyaltyOfferClass/LoyaltyOfferCreator/loyaltyOfferCreator";
import {
  PromoContent,
  PromoContentType,
  PromoPopupDirective,
  PromoPopupType,
  PromoTierDirection,
} from "./promoMaterialTypes";

/**
 * Which modal a directive becomes — decided here, in one pure function, so the
 * mapping can be tested without React, Redux or the modal system.
 *
 * The invariant this file exists to protect: a directive that cannot be rendered
 * resolves to {@link PromoPopupNothing} and is therefore never *recorded* as
 * shown. The server spends a once-per-session (or once-ever) slot the moment a
 * display is reported, so reporting one the customer never saw would silently
 * burn the pop-up.
 */
type PromoPopupResolution = PromoPopupModal | PromoPopupNothing;

/** Same reasoning as PromoBannerKind: the discriminant is written many times. */
export enum PromoPopupResolutionKind {
  MODAL = "modal",
  NOTHING = "nothing",
}

/** Ready to open: the modal system's component path plus its props. */
type PromoPopupModal = {
  kind: PromoPopupResolutionKind.MODAL;
  /** Path under `components/global/Modal/Modals/`. */
  component: string;
  componentProps: Record<string, unknown>;
};

/** Nothing to show. [reason] is for the log, never for the customer. */
type PromoPopupNothing = {
  kind: PromoPopupResolutionKind.NOTHING;
  reason: string;
};

const nothing = (reason: string): PromoPopupNothing => ({
  kind: PromoPopupResolutionKind.NOTHING,
  reason,
});

/** The campaign's authored content on a surface of its own. */
const dressing = (content: PromoContent): PromoPopupModal => ({
  kind: PromoPopupResolutionKind.MODAL,
  component: "PromoAnnouncementPopup/PromoAnnouncementPopup",
  componentProps: { content },
});

/** A campaign's words, in the shape the redeem dialog takes them in. */
const redeemCopy = (content?: PromoContent): LoyaltyRedeemCopy | undefined =>
  content?.type === PromoContentType.TEXT
    ? {
        title: content.headline,
        description: content.description,
        buttonText: content.buttonText,
      }
    : undefined;

/**
 * The server's movement directions, in the vocabulary the existing
 * LoyaltyTierChange pop-up already speaks. MIGRATION never reaches a client.
 */
const TIER_CHANGE_KIND: Record<PromoTierDirection, TierChangeKind> = {
  [PromoTierDirection.UPGRADE]: TierChangeKind.UPGRADE,
  [PromoTierDirection.SAME_TIER]: TierChangeKind.STEADY,
  [PromoTierDirection.DOWNGRADE]: TierChangeKind.DOWNGRADE,
};

/**
 * [offers] is the customer's own loaded offer list — every offer-bearing
 * directive names its subject by uuid for exactly this lookup. The one exception
 * is DOWNLOAD_APP, whose offer arrives inline because an app-redeemable offer is
 * absent from this channel's list.
 */
export const resolvePromoPopup = (
  directive: PromoPopupDirective,
  offers: LoyaltyOffer[],
  context: {
    downloadAppImage?: string;
    restaurantId?: number;
    /** Tier uuids the tier system has actually loaded — see TIER_MOVEMENT. */
    loadedTierIds?: string[];
  }
): PromoPopupResolution => {
  const byId = (uuid?: string): LoyaltyOffer | undefined =>
    uuid ? offers.find((offer) => offer.id === uuid) : undefined;

  switch (directive.type) {
    case PromoPopupType.REWARD: {
      const content = directive.content;

      // The redeem dialog draws the offer's own icon and has nowhere to put
      // campaign artwork, so an IMAGE keeps a surface of its own. Dropping it in
      // favour of the offer's copy is the bug custom-mobile-apps#588 fixed.
      if (content?.type === PromoContentType.IMAGE) {
        return dressing(content);
      }

      // Otherwise the ordinary redeem dialog, which needs the offer — so a list
      // miss falls back to the authored words rather than losing the pop-up.
      const offer = byId(directive.offerId);
      if (!offer) {
        return content
          ? dressing(content)
          : nothing(`REWARD offer ${directive.offerId} is not in the list`);
      }

      const copy = redeemCopy(content);

      return {
        kind: PromoPopupResolutionKind.MODAL,
        component: "LoyaltyRedeemPopup/LoyaltyRedeemPopup",
        componentProps: {
          offer,
          // Pushed, not already applied: the button reads "Redeem" and applies
          // it, as the pre-migration home pop-up did.
          isActiveOffer: false,
          ...(copy && { copy }),
        },
      };
    }

    case PromoPopupType.ALL_REWARDS: {
      // The dialog reads the customer's applicable offers from Redux itself, so
      // the uuids are a cross-check rather than the source: if none of them
      // resolve, the dialog would open empty.
      const resolved = (directive.offerIds ?? []).filter((id) => byId(id));
      if (resolved.length === 0) {
        return nothing("ALL_REWARDS covered no offer this client holds");
      }

      return {
        kind: PromoPopupResolutionKind.MODAL,
        component: "LoyaltyOffersPopup/LoyaltyOffersPopup",
        componentProps: {},
      };
    }

    case PromoPopupType.OFFER_UNAVAILABLE: {
      // Deliberately renderable with no offers left: "the offer you came for no
      // longer applies" is the message, and the picker degrades to copy plus an
      // acknowledge button when there is nothing to switch to.
      return {
        kind: PromoPopupResolutionKind.MODAL,
        component: "LoyaltyOffersPopup/LoyaltyOffersPopup",
        componentProps: { unavailableOfferId: directive.offerId ?? null },
      };
    }

    case PromoPopupType.DOWNLOAD_APP: {
      // The pop-up's own artwork is restaurant config, not campaign content.
      const offer = directive.offer
        ? createLoyaltyOffer(directive.offer)
        : undefined;

      return {
        kind: PromoPopupResolutionKind.MODAL,
        component: "DownloadAppPopup/DownloadAppPopup",
        componentProps: {
          screenshotImage: context.downloadAppImage,
          restaurantId: context.restaurantId,
          ...(offer && { loyaltyOffer: offer }),
        },
      };
    }

    case PromoPopupType.ANNOUNCEMENT:
      return directive.content
        ? dressing(directive.content)
        : nothing("ANNOUNCEMENT carried no content to render");

    case PromoPopupType.TIER_MOVEMENT: {
      const movement = directive.tierMovement;
      if (!movement) {
        return nothing("TIER_MOVEMENT carried no movement to render");
      }

      // Annotated as possibly-absent on purpose: the key type makes the map
      // exhaustive at the declaration above, but the value still arrives off the
      // wire, so a direction this build has never heard of must fall through to
      // "one fewer pop-up" rather than open a pop-up with no copy.
      const kind: TierChangeKind | undefined =
        TIER_CHANGE_KIND[movement.direction];
      if (!kind) {
        return nothing(`unknown tier direction ${movement.direction}`);
      }

      // LoyaltyTierChange draws the tier from the loaded tier system — its
      // rank, colour and earning rate — and renders nothing at all when the tier
      // is missing. Checking here instead means an unloaded tier system leaves
      // the movement unshown *and unrecorded*, so the server keeps it pending
      // for the next visit rather than retiring an empty dialog.
      if (!context.loadedTierIds?.includes(movement.toTier.id)) {
        return nothing(
          `tier ${movement.toTier.id} is not in the loaded tier system`
        );
      }

      return {
        kind: PromoPopupResolutionKind.MODAL,
        component: "LoyaltyTierChange/LoyaltyTierChange",
        componentProps: {
          kind,
          tierId: movement.toTier.id,
          // The moved-down artwork animates the medal off the tower it fell
          // from, so it wants the origin as well as the destination. Optional
          // because it arrives off the wire: without it the drop is drawn as the
          // single step the design shows.
          ...(movement.fromTier?.id && { fromTierId: movement.fromTier.id }),
        },
      };
    }

    default:
      // A type this build does not know: one fewer pop-up, never a crash.
      return nothing(`unknown pop-up type ${String(directive.type)}`);
  }
};

/**
 * What to report when a pop-up becomes visible — `undefined` when this display
 * is the device's business alone.
 *
 * Only the durable kinds reach the server: an admin-authored material (keyed by
 * `materialId`), a tier movement (retired by echoing `tierHistoryId`), and
 * DOWNLOAD_APP (whose cooldown outlives the session). The session families ride
 * the `shown` hints instead — the server knowingly drops a report of them, so
 * sending one would be a wasted request.
 */
export const durableDisplayOf = (
  directive: PromoPopupDirective
):
  | {
      materialId?: string;
      tierHistoryId?: number;
      offerId?: string;
    }
  | undefined => {
  if (directive.materialId) {
    return {
      materialId: directive.materialId,
      ...(directive.offerId && { offerId: directive.offerId }),
    };
  }
  if (directive.tierMovement) {
    return { tierHistoryId: directive.tierMovement.tierHistoryId };
  }
  if (directive.type === PromoPopupType.DOWNLOAD_APP) {
    return {};
  }

  return undefined;
};
